/**
 * The wizard's pictures: an emoji per animal and theme (FD §3.2 step 4 [A]: an icon, no photos),
 * and small drawings for the land, path and water cards.
 */
import type { LayoutStyle, Species, Theme, WaterStyle } from '../../../shared/types';

// Matched against the species id, first hit wins; more specific words go first.
const ANIMAL_EMOJI: [string, string][] = [
  ['lionfish', '🐠'], ['tigerfish', '🐟'], ['penguin', '🐧'], ['flamingo', '🦩'], ['parakeet', '🦜'], ['toucan', '🦜'], ['hornbill', '🦜'], ['magpie', '🐦'],
  ['long-eared-owl', '🦉'], ['eagle-ray', '🐟'], ['eagle', '🦅'], ['vulture', '🦅'], ['secretarybird', '🦅'], ['shoebill', '🐦'],
  ['bittern', '🐦'], ['ostrich', '🐦'], ['lion-tamarin', '🐒'], ['lion', '🦁'], ['leopard-shark', '🦈'], ['shark', '🦈'],
  ['hammerhead', '🦈'], ['leopard', '🐆'], ['tiger', '🐅'], ['lynx', '🐈'], ['elephant', '🐘'], ['giraffe', '🦒'],
  ['zebra', '🦓'], ['przewalski', '🐎'], ['hippo', '🦛'], ['crocodile', '🐊'], ['gharial', '🐊'], ['monitor', '🦎'],
  ['komodo', '🦎'], ['tortoise', '🐢'], ['turtle', '🐢'], ['gorilla', '🦍'], ['orangutan', '🦧'], ['chimpanzee', '🐒'],
  ['siamang', '🐒'], ['lemur', '🐒'], ['panda', '🐼'], ['bear', '🐻'], ['wolf', '🐺'], ['wild-dog', '🐕'], ['hyena', '🐕'],
  ['camel', '🐫'], ['bison', '🦬'], ['buffalo', '🐃'], ['wildebeest', '🐃'], ['boar', '🐗'], ['warthog', '🐗'],
  ['babirusa', '🐗'], ['deer', '🦌'], ['gazelle', '🦌'], ['springbok', '🦌'], ['gemsbok', '🦌'], ['nyala', '🦌'],
  ['antelope', '🦌'], ['kangaroo', '🦘'], ['beaver', '🦫'], ['hare', '🐇'], ['meerkat', '🦦'], ['aardvark', '🐜'],
  ['pangolin', '🦔'], ['tapir', '🐖'], ['manatee', '🦭'], ['puffer', '🐡'], ['lionfish', '🐠'], ['anemonefish', '🐠'],
  ['tang', '🐠'], ['surgeonfish', '🐠'], ['damselfish', '🐠'], ['wrasse', '🐠'], ['triggerfish', '🐠'], ['batfish', '🐠'],
  ['bannerfish', '🐠'], ['sweetlips', '🐠'], ['sergeant', '🐠'], ['fusilier', '🐠'], ['unicornfish', '🐠'],
  ['snapper', '🐠'], ['cichlid', '🐠'], ['rainbowfish', '🐠'], ['loach', '🐠'], ['gourami', '🐠'],
];
const KIND_EMOJI: Record<Species['kind'], string> = { habitat: '🐾', aviary: '🐦', aquarium: '🐟' };

/** An emoji for an animal: by its name where we know one, else by habitat / aviary / aquarium. */
export function animalEmoji(sp: Pick<Species, 'id' | 'kind'>): string {
  return ANIMAL_EMOJI.find(([k]) => sp.id.includes(k))?.[1] ?? KIND_EMOJI[sp.kind];
}

const THEME_EMOJI: Record<string, string> = {
  'african-savanna': '🦁',
  'african-rainforest': '🦍',
  'asian-jungle': '🐅',
  'himalaya-mountains': '🐼',
  'northern-forest-tundra': '🐻',
  'european-woodland': '🦌',
  'desert-drylands': '🐫',
  'wetlands-rivers': '🦛',
  'bird-house': '🦜',
  'coral-reef-aquarium': '🐠',
  'open-ocean-aquarium': '🦈',
  'freshwater-aquarium': '🐟',
};

/** The big emoji on a theme card. */
export function themeEmoji(t: Theme): string {
  return THEME_EMOJI[t.id] ?? (t.biome === 'aquatic' ? '🌊' : '🌳');
}

/** A soft background per biome for the theme cards (CSS gradient using the map colours). */
export function biomeBackground(t: Theme): string {
  const c: Record<Theme['biome'], string> = {
    grassland: '#e9d48c',
    tropical: '#7fbf7a',
    temperate: '#a9c98d',
    desert: '#e8c08f',
    taiga: '#9fbfae',
    tundra: '#c9dde6',
    aquatic: '#86c3e0',
  };
  return `linear-gradient(135deg, ${c[t.biome]}, color-mix(in srgb, ${c[t.biome]} 45%, var(--surface)))`;
}

const STROKE = { fill: 'none', stroke: 'var(--map-road)', strokeWidth: 7, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/** The picture on a path-style card. */
export function PathPicture({ style }: { style: LayoutStyle | 'surprise' }) {
  return (
    <svg viewBox="0 0 120 90" className="pic" aria-hidden="true">
      <rect x="4" y="4" width="112" height="82" rx="26" fill="var(--map-plot)" />
      {style === 'loop' && <ellipse cx="60" cy="45" rx="40" ry="26" {...STROKE} />}
      {style === 'cross' && (
        <>
          <ellipse cx="60" cy="45" rx="40" ry="26" {...STROKE} />
          <path d="M60 19 C 52 35, 68 55, 60 71" {...STROKE} />
        </>
      )}
      {style === 'eight' && (
        <>
          <ellipse cx="38" cy="45" rx="22" ry="25" {...STROKE} />
          <ellipse cx="82" cy="45" rx="22" ry="25" {...STROKE} />
        </>
      )}
      {style === 'surprise' && <Dice />}
      <rect x="54" y="72" width="12" height="10" rx="2" fill="var(--accent)" />
    </svg>
  );
}

/** The picture on a water card. */
export function WaterPicture({ style }: { style: WaterStyle | 'surprise' }) {
  const lake = <path d="M40 30 C 58 22, 80 30, 78 44 C 76 58, 52 62, 42 54 C 32 47, 30 35, 40 30 Z" fill="var(--map-water)" />;
  const river = <path d="M8 22 C 35 30, 30 60, 60 64 S 98 50, 114 74" fill="none" stroke="var(--map-water)" strokeWidth="9" strokeLinecap="round" />;
  return (
    <svg viewBox="0 0 120 90" className="pic" aria-hidden="true">
      <rect x="4" y="4" width="112" height="82" rx="26" fill="var(--map-plot)" />
      {(style === 'lake' || style === 'both') && lake}
      {(style === 'river' || style === 'both') && river}
      {style === 'none' && (
        <>
          <circle cx="40" cy="40" r="10" fill="var(--map-habitat)" />
          <circle cx="72" cy="52" r="13" fill="var(--map-habitat)" />
          <circle cx="86" cy="30" r="7" fill="var(--map-habitat)" />
        </>
      )}
      {style === 'surprise' && <Dice />}
    </svg>
  );
}

function Dice() {
  return (
    <g>
      <rect x="40" y="25" width="40" height="40" rx="8" fill="var(--surface)" stroke="var(--map-edge)" strokeWidth="2" />
      {[
        [50, 35], [70, 35], [60, 45], [50, 55], [70, 55],
      ].map(([x, y]) => (
        <circle key={`${x},${y}`} cx={x} cy={y} r="3.6" fill="var(--text)" />
      ))}
    </g>
  );
}

/** The picture on the "blank land" card, scaled for Small / Medium / Large. */
export function LandPicture({ scale }: { scale: number }) {
  const r = 14 + 26 * scale;
  return (
    <svg viewBox="0 0 120 90" className="pic" aria-hidden="true">
      <path
        d={`M${60 - r} 45 C ${60 - r} ${45 - r * 0.8}, ${60 + r * 0.9} ${45 - r * 0.85}, ${60 + r} 43 C ${60 + r * 1.05} ${45 + r * 0.8}, ${60 - r * 0.8} ${45 + r * 0.85}, ${60 - r} 45 Z`}
        fill="var(--map-plot)"
        stroke="var(--map-edge)"
        strokeWidth="1.5"
      />
    </svg>
  );
}
